/**
 * The top-level routes that make up the sign-in flow, as declared in `app.routing.ts`.
 *
 * `force-log-out` is listed explicitly. A substring search used to catch it for free as a
 * by-product of matching `log-out`; exact matching does not, and dropping it would make the
 * forced-sign-out page a legitimate page to return someone to.
 */
const AUTH_ROUTES = ['login', 'log-out', 'force-log-out', 'redirect', 'auth-error'];

/**
 * True if `url` addresses one of the sign-in routes.
 *
 * Sending a user back to one of these once they are signed in would bounce them straight through
 * the flow they have just completed, so callers treat them as "no page to return to".
 *
 * Matches the first path segment exactly rather than searching the whole URL. A substring search
 * counts an ordinary page as an auth route whenever one of these words appears anywhere in it,
 * including in its query string or fragment — `/reports/index?mode=redirect` reads as the MSAL
 * redirect page — and the user is quietly sent to the homepage instead of back to their page.
 */
export function isAuthRoute(url: string): boolean {
  // Strip the query and fragment first: only the path decides which route this is, and either
  // part can legitimately contain one of the words above.
  const [firstSegment] = url.split(/[?#]/)[0].split('/').filter(Boolean);

  // Matrix parameters hang off the segment itself (`/login;from=x`), so they have to come off
  // before the comparison. The old substring search tolerated them by accident; exact matching
  // would read `login;from=x` as an ordinary page and send a signed-in user back to the sign-in
  // screen. Nothing in the app writes them today — this keeps the helper honest for callers that
  // might.
  const route = firstSegment?.split(';')[0];

  return route !== undefined && AUTH_ROUTES.includes(route.toLowerCase());
}
